import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, KeyRound } from "lucide-react";

const NAVY = "#0b192c", MUTED = "#475569";

function formatErr(d) {
  if (d == null) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export default function ResetPassword() {
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password/verify`,
              { params: { token }, withCredentials: true })
      .then(({ data }) => { setValid(!!data.valid); setEmail(data.email || ""); })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 10) return toast.error("Password must be at least 10 characters");
    if (pwd !== pwd2) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        { token, new_password: pwd },
        { withCredentials: true }
      );
      setDone(true);
      toast.success("Password updated · redirecting to sign in…");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#fdfcf8" }}>
      <div className="w-full max-w-md p-8" data-testid="reset-password-page">
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: MUTED }}>Password reset</div>
        <h1 className="font-serif text-4xl mt-2 leading-none" style={{ color: NAVY }}>Choose a new password</h1>

        {checking && (
          <p className="text-sm mt-4" style={{ color: MUTED }}>Verifying link…</p>
        )}

        {!checking && !valid && (
          <div className="mt-8 p-5 border rounded-lg" style={{ background: "#fef2f2", borderColor: "#fecaca" }}
               data-testid="reset-invalid">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#991b1b" }}>
              <XCircle size={16} /> Link is invalid or expired
            </div>
            <p className="text-sm mt-2" style={{ color: MUTED }}>
              Reset links expire 30 minutes after they&apos;re issued and can only be used once.
              Request a fresh one.
            </p>
            <Link to="/forgot-password">
              <Button className="mt-4 bg-[#0b192c] text-white hover:bg-[#0b192c]/90"
                      data-testid="reset-new-link-btn">
                Request a new link
              </Button>
            </Link>
          </div>
        )}

        {!checking && valid && !done && (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <p className="text-sm" style={{ color: MUTED }}>
              Signing in as <b data-testid="reset-target-email">{email}</b>.
            </p>
            <div>
              <Label htmlFor="pwd" className="text-xs font-semibold uppercase tracking-wider" style={{ color: MUTED }}>
                New password (min 10 characters)
              </Label>
              <Input id="pwd" type="password" required value={pwd}
                     onChange={(e) => setPwd(e.target.value)}
                     className="mt-1.5 bg-white border-[#e2e1dd]"
                     data-testid="reset-pwd-input" />
            </div>
            <div>
              <Label htmlFor="pwd2" className="text-xs font-semibold uppercase tracking-wider" style={{ color: MUTED }}>
                Confirm new password
              </Label>
              <Input id="pwd2" type="password" required value={pwd2}
                     onChange={(e) => setPwd2(e.target.value)}
                     className="mt-1.5 bg-white border-[#e2e1dd]"
                     data-testid="reset-pwd2-input" />
            </div>
            <Button type="submit" disabled={loading}
                    className="w-full bg-[#0b192c] hover:bg-[#0b192c]/90 text-white font-semibold h-11"
                    data-testid="reset-submit-btn">
              <KeyRound size={13} className="mr-1.5" />
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}

        {done && (
          <div className="mt-8 p-5 border rounded-lg" style={{ background: "#f5faf5", borderColor: "#c7e8c9" }}
               data-testid="reset-done">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#166534" }}>
              <CheckCircle2 size={16} /> Password updated
            </div>
            <p className="text-sm mt-2" style={{ color: MUTED }}>Redirecting to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
